/**
 * Who else edited a live document's history entry (docs/DOCUMENTS.md §12: a slice of about five minutes lists
 * everyone who edited in it, its author the first): "with Bo and Claude" after the author, people and agents with
 * their pictures as everywhere. Compact in lists (the first few, then how many more), everyone on a change's page.
 */
import type { Commit } from "../../api/types.gen.ts";
import { locale, SLOT, t } from "../../i18n/index.ts";
import { displayName } from "../../lib/people.ts";
import { Person } from "../../ui/Avatar.tsx";
import { Slotted } from "../../ui/misc.tsx";

/** How many a list names before "and N more". */
const NAMED = 2;

/** The others who edited it beside its author (its first editor), with their names. */
function coEditors(c: Pick<Commit, "author" | "editors" | "editor_names">): { id: string; name: string }[] {
  const names = c.editor_names ?? [];
  return (c.editors ?? [])
    .map((id, i) => ({ id, name: names[i] ?? "" }))
    .filter((e, i) => !(i === 0 || e.id === c.author));
}

/** "Bo, Claude and Di", in the reader's language, with a slot for each part (SLOT). */
const listOf = (parts: readonly string[]): string =>
  new Intl.ListFormat(locale.value, { style: "long", type: "conjunction" }).format(parts);

/** "with Bo and Claude" after a change's author; nothing when it was theirs alone. `full`: everyone named. */
export function WithEditors({
  c,
  full = false,
  class: cls,
}: {
  c: Pick<Commit, "author" | "editors" | "editor_names">;
  full?: boolean;
  class?: string;
}) {
  const others = coEditors(c);
  if (others.length === 0) {
    return null;
  }
  const named = full || others.length <= NAMED + 1 ? others : others.slice(0, NAMED);
  const more = others.length - named.length;
  const parts = [...named.map(() => SLOT), ...(more > 0 ? [t("history.moreEditors", { count: more })] : [])];
  const everyone = others.map((e) => displayName(e.id, e.name)).join(", ");
  return (
    <span class={cls} title={full ? undefined : everyone} data-editors={others.length}>
      <Slotted text={t("history.withEditors", { names: listOf(parts) })}>
        {named.map((e) => (
          <Person key={e.id} id={e.id} name={e.name} size={full ? 20 : 14} />
        ))}
      </Slotted>
    </span>
  );
}
